import type { DrawingDocument, TextAnnotation } from '../model';
import type { EditorSelection } from './editor.ts';
import {
  alignObjects,
  annotationBounds,
  translateAnnotations,
  type AlignKind,
  type AlignOptions,
  type ReactionParts
} from './objectAlignment.ts';
import { moveAtoms } from './atomGeometryMutations.ts';
import { moleculeEdges } from './documentSelectors.ts';
import { saltsAsOne } from './ionicPairs.ts';
import {
  arrowFrame,
  boxMembershipAsDrawn,
  centreOf,
  REACTION_ZONES,
  translateRect,
  defaultBoxes,
  moleculeComponents,
  plusSignPositions,
  PLUS_JOINED_ZONES,
  zoneOf,
  type Point,
  type ReactionArrowGeometry,
  type ReactionBoxes,
  type ReactionZone,
  type ReactionZoneBox,
  type Rect
} from './reactionGraph.ts';

// ADR 0014 / ADR 0025 -- the reaction boxes' pure geometry, shared by the paper (useReactionBoxes),
// the pictures of a reaction (cards, /r/) and the render service.

/** A reaction arrow's shaft ends in document units (its anchors may be points, atoms or bonds). */
export function arrowOf(document: DrawingDocument): ReactionArrowGeometry | null {
  const arrow = document.annotations.find((annotation) => annotation.kind === 'reaction-arrow');
  if (!arrow || arrow.kind !== 'reaction-arrow') return null;
  const { coordinates } = document.depiction.coordinateSet;
  const edges = document.structure.graph.edges;
  const resolve = (anchor: (typeof arrow)['from']): Point | null => {
    switch (anchor.kind) {
      case 'point':
        return anchor.at;
      case 'atom':
        return coordinates[anchor.atomId] ?? null;
      case 'bond': {
        const edge = edges.find((candidate) => candidate.id === anchor.bondId);
        const a = edge && coordinates[edge.source];
        const b = edge && coordinates[edge.target];
        return a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : null;
      }
      default:
        return null;
    }
  };
  const from = resolve(arrow.from);
  const to = resolve(arrow.to);
  return from && to ? { id: arrow.id, from, to } : null;
}

export function componentsOf(document: DrawingDocument) {
  const { coordinates } = document.depiction.coordinateSet;
  return moleculeComponents(
    document.structure.graph.nodes.map((node) => node.id),
    moleculeEdges(document.structure),
    (id) => coordinates[id]
  );
}

/** Sizes in document units, relative to the drawing's typical bond length. */
export function boxScale(document: DrawingDocument) {
  const { coordinates } = document.depiction.coordinateSet;
  const lengths = document.structure.graph.edges
    .map((edge) => {
      const a = coordinates[edge.source];
      const b = coordinates[edge.target];
      return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
    })
    .filter((length) => length > 0)
    .sort((a, b) => a - b);
  const bond = lengths[Math.floor(lengths.length / 2)] ?? 40;
  return {
    padding: bond * 0.6,
    gap: bond * 0.6,
    minPlaceholder: bond * 1.2,
    minWidth: bond * 3.2,
    minHeight: bond * 2.6
  };
}

/**
 * #1141 (ADR 0025) -- where the paper draws its "+" signs (#739), in drawing units: between the
 * reactants and between the products, a salt counting as one (#1134). `boxes` are the paper's own
 * when it has them; a reaction reopened elsewhere (a card, the /r/ page) gets the boxes it would
 * get on the paper. Empty without a reaction arrow.
 */
export function reactionPlusSigns(document: DrawingDocument, boxes?: ReactionBoxes | null): Point[] {
  const arrow = arrowOf(document);
  if (!arrow) return [];
  const components = componentsOf(document);
  const scale = boxScale(document);
  const { molecules } = boxMembershipAsDrawn(
    components,
    boxes ?? defaultBoxes(components, arrow, scale),
    scale.padding
  );
  const joined = saltsAsOne(document, molecules);
  return PLUS_JOINED_ZONES.flatMap((zone) => plusSignPositions(joined.filter((molecule) => molecule.zone === zone)));
}

/**
 * #1137 -- the boxes as the paper draws them (grown around their molecules, #1134): the paper's
 * own when given, else those the reaction would get on the paper. Null without a reaction arrow.
 */
export function drawnReactionBoxes(
  document: DrawingDocument,
  boxes?: ReactionBoxes | null
): Readonly<Record<ReactionZone, ReactionZoneBox>> | null {
  const arrow = arrowOf(document);
  if (!arrow) return null;
  const components = componentsOf(document);
  const scale = boxScale(document);
  return boxMembershipAsDrawn(components, boxes ?? defaultBoxes(components, arrow, scale), scale.padding).shown;
}

/** One text that is part of the reaction, and the side of the arrow it is on. */
export interface ReactionText {
  readonly annotation: TextAnnotation;
  /** Above the arrow: reagents and catalysts; below: solvents (and conditions either side). */
  readonly zone: 'above' | 'below';
}

/**
 * ADR 0014 amendment (#1137, owner) -- the texts that are part of the reaction: in the Reagents &
 * catalysts or Solvents box (as drawn), or touching the arrow. Titles and captions elsewhere on
 * the paper, and callouts pointing at something, are left alone. `shown` are the boxes as the
 * paper draws them (its own, possibly moved); by default those the reaction would get.
 */
export function reactionTexts(
  document: DrawingDocument,
  shown: Readonly<Record<ReactionZone, ReactionZoneBox>> | null = drawnReactionBoxes(document)
): ReactionText[] {
  const arrow = arrowOf(document);
  if (!arrow || !shown) return [];
  const bounds = annotationBounds(document);
  const reach = boxScale(document).gap;
  const inside = (point: Point, zone: 'above' | 'below') => {
    const { rect } = shown[zone];
    return point.x >= rect.minX && point.x <= rect.maxX && point.y >= rect.minY && point.y <= rect.maxY;
  };
  const texts: ReactionText[] = [];
  for (const annotation of document.annotations) {
    if (annotation.kind !== 'text' || annotation.attachment) continue;
    const box = bounds.get(annotation.id);
    if (!box) continue;
    const centre = { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2 };
    const zone = inside(centre, 'above') ? 'above' : inside(centre, 'below') ? 'below' : null;
    if (zone) {
      texts.push({ annotation, zone });
      continue;
    }
    // Touching the arrow: its box comes within reach of the shaft, beside it rather than past an end.
    const side = zoneOf(centre, arrow.from, arrow.to);
    if ((side === 'above' || side === 'below') && distanceToBox(arrow.from, arrow.to, box) <= reach) {
      texts.push({ annotation, zone: side });
    }
  }
  return texts;
}

/** The shortest distance from the segment a-b to a box (0 when they cross). */
function distanceToBox(
  a: Point,
  b: Point,
  box: { readonly minX: number; readonly minY: number; readonly maxX: number; readonly maxY: number }
): number {
  const steps = 24;
  let best = Infinity;
  for (let index = 0; index <= steps; index++) {
    const t = index / steps;
    const x = a.x + (b.x - a.x) * t;
    const y = a.y + (b.y - a.y) * t;
    const dx = Math.max(box.minX - x, 0, x - box.maxX);
    const dy = Math.max(box.minY - y, 0, y - box.maxY);
    best = Math.min(best, Math.hypot(dx, dy));
  }
  return best;
}

/**
 * #1137 follow-up (owner, 2026-10-31) -- what was just finished or dropped in the Reagents &
 * catalysts or Solvents box settles by the arrow: centred on the shaft, just clear of it, or of
 * what already sits there. Texts by `annotationIds`; structures only when every atom moved (part
 * of a structure dragged is an edit, not a drop). The same document when nothing settles.
 */
export function settledByArrow(
  document: DrawingDocument,
  atomIds: Iterable<string>,
  annotationIds: Iterable<string>,
  boxes?: ReactionBoxes | null
): DrawingDocument {
  const arrow = arrowOf(document);
  if (!arrow) return document;
  const components = componentsOf(document);
  const scale = boxScale(document);
  const { molecules, shown } = boxMembershipAsDrawn(
    components,
    boxes ?? defaultBoxes(components, arrow, scale),
    scale.padding
  );
  const movedAtoms = new Set(atomIds);
  const movedTexts = new Set(annotationIds);
  const textBounds = annotationBounds(document);
  // Atom labels reach past their atoms' centres.
  const label = scale.gap / 2;
  interface Item {
    readonly zone: 'above' | 'below';
    readonly bounds: Rect;
    readonly moved: boolean;
    readonly textId?: string;
    readonly atomIds?: readonly string[];
  }
  const items: Item[] = [
    ...reactionTexts(document, shown).flatMap(({ annotation, zone }): Item[] => {
      const bounds = textBounds.get(annotation.id);
      return bounds ? [{ zone, bounds, moved: movedTexts.has(annotation.id), textId: annotation.id }] : [];
    }),
    ...molecules.flatMap((molecule): Item[] =>
      molecule.zone === 'above' || molecule.zone === 'below'
        ? [
            {
              zone: molecule.zone,
              bounds: {
                minX: molecule.bounds.minX - label,
                minY: molecule.bounds.minY - label,
                maxX: molecule.bounds.maxX + label,
                maxY: molecule.bounds.maxY + label
              },
              moved: molecule.atomIds.length > 0 && molecule.atomIds.every((id) => movedAtoms.has(id)),
              atomIds: molecule.atomIds
            }
          ]
        : []
    )
  ];
  if (!items.some((item) => item.moved)) return document;

  const { axis, normal } = arrowFrame(arrow.from, arrow.to);
  const middle = { x: (arrow.from.x + arrow.to.x) / 2, y: (arrow.from.y + arrow.to.y) / 2 };
  const gap = scale.gap / 2;
  /** Half a box's extent along a direction. */
  const half = (bounds: Rect, along: Point) =>
    ((bounds.maxX - bounds.minX) / 2) * Math.abs(along.x) + ((bounds.maxY - bounds.minY) / 2) * Math.abs(along.y);
  const placed = items.filter((item) => !item.moved).map((item) => ({ zone: item.zone, bounds: item.bounds }));
  let next = document;
  for (const item of items.filter((candidate) => candidate.moved)) {
    const out = item.zone === 'above' ? normal : { x: -normal.x, y: -normal.y };
    const across = half(item.bounds, out);
    const sideways = half(item.bounds, axis);
    // From the shaft outwards: past every box already there that this one would overlap.
    let distance = gap + across;
    for (let pass = 0; pass <= placed.length; pass++) {
      const blocking = placed.find(({ zone, bounds }) => {
        if (zone !== item.zone) return false;
        const offset = { x: centreOf(bounds).x - middle.x, y: centreOf(bounds).y - middle.y };
        const along = offset.x * axis.x + offset.y * axis.y;
        const outwards = offset.x * out.x + offset.y * out.y;
        return (
          Math.abs(along) < sideways + half(bounds, axis) && Math.abs(outwards - distance) < across + half(bounds, out)
        );
      });
      if (!blocking) break;
      const offset = { x: centreOf(blocking.bounds).x - middle.x, y: centreOf(blocking.bounds).y - middle.y };
      distance = offset.x * out.x + offset.y * out.y + half(blocking.bounds, out) + gap + across;
    }
    const centre = centreOf(item.bounds);
    const dx = middle.x + out.x * distance - centre.x;
    const dy = middle.y + out.y * distance - centre.y;
    placed.push({
      zone: item.zone,
      bounds: {
        minX: item.bounds.minX + dx,
        minY: item.bounds.minY + dy,
        maxX: item.bounds.maxX + dx,
        maxY: item.bounds.maxY + dy
      }
    });
    if (Math.hypot(dx, dy) < 0.5) continue;
    if (item.textId) {
      next = translateAnnotations(next, new Set([item.textId]), dx, dy);
    } else if (item.atomIds) {
      const { width, height, coordinates } = next.depiction.coordinateSet;
      next = moveAtoms(
        next,
        Object.fromEntries(
          item.atomIds.map((id) => [
            id,
            { x: (coordinates[id]!.x + dx) / width, y: (coordinates[id]!.y + dy) / height }
          ])
        )
      );
    }
  }
  return next;
}

/**
 * #1151 -- the reaction's parts for Align and Distribute: its arrow, and what each box holds as
 * the paper draws it (a salt's ions together, the texts by the arrow included). Null without an
 * arrow.
 */
export function reactionParts(document: DrawingDocument, boxes?: ReactionBoxes | null): ReactionParts | null {
  const arrow = arrowOf(document);
  if (!arrow) return null;
  const components = componentsOf(document);
  const scale = boxScale(document);
  const { molecules, shown } = boxMembershipAsDrawn(
    components,
    boxes ?? defaultBoxes(components, arrow, scale),
    scale.padding
  );
  return {
    arrowId: arrow.id,
    molecules: saltsAsOne(document, molecules).flatMap(({ zone, atomIds }) => (zone ? [{ zone, atomIds }] : [])),
    texts: reactionTexts(document, shown).map(({ annotation, zone }) => ({ annotationId: annotation.id, zone }))
  };
}

/**
 * #1151 (owner) -- Align or Distribute with the reaction arranged as such (see `alignObjects`),
 * each box moved with what it holds: by the average move of its contents, an empty box with the
 * arrow. `boxes` are the paper's own; they come back moved, for the same undo step.
 */
export function alignWithReaction(
  document: DrawingDocument,
  selection: EditorSelection,
  kind: AlignKind,
  options: AlignOptions,
  boxes: ReactionBoxes
): { readonly document: DrawingDocument; readonly boxes: ReactionBoxes } {
  const reaction = reactionParts(document, boxes);
  const next = alignObjects(document, selection, kind, { ...options, reaction });
  if (next === document || !reaction) return { document: next, boxes };
  const before = document.depiction.coordinateSet.coordinates;
  const after = next.depiction.coordinateSet.coordinates;
  const textAt = (doc: DrawingDocument, id: string) => {
    const annotation = doc.annotations.find((candidate) => candidate.id === id);
    return annotation?.kind === 'text' ? annotation.position : null;
  };
  const moveOf = (from: Point | null | undefined, to: Point | null | undefined) =>
    from && to ? { x: to.x - from.x, y: to.y - from.y } : { x: 0, y: 0 };
  const arrowMove = moveOf(arrowOf(document)?.from, arrowOf(next)?.from);
  const moved = { ...boxes };
  for (const zone of REACTION_ZONES) {
    const moves = [
      ...reaction.molecules
        .filter((molecule) => molecule.zone === zone)
        .map(({ atomIds }) => moveOf(before[atomIds[0]!], after[atomIds[0]!])),
      ...reaction.texts
        .filter((text) => text.zone === zone)
        .map(({ annotationId }) => moveOf(textAt(document, annotationId), textAt(next, annotationId)))
    ];
    const { x, y } = moves.length
      ? {
          x: moves.reduce((sum, move) => sum + move.x, 0) / moves.length,
          y: moves.reduce((sum, move) => sum + move.y, 0) / moves.length
        }
      : arrowMove;
    moved[zone] = translateRect(boxes[zone], x, y);
  }
  return { document: next, boxes: moved };
}
